import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { OnboardingData } from '../CompanyOnboardingWizard';

interface Permission {
    id?: number;
    name?: string;
    '@id'?: string;
}

interface Product {
    id: number;
    name: string;
    description?: string;
    permissions?: Permission[];
}

interface LicenseStepProps {
    data: OnboardingData;
    updateData: (updates: Partial<OnboardingData>) => void;
    errors: Record<string, string>;
}

export const LicenseStep: React.FC<LicenseStepProps> = ({ data, updateData, errors }) => {
    const translate = useTranslate();

    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [hasExpiration, setHasExpiration] = useState(false);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                // Fetch products directly to preserve nested permission data
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_SSO_URL}/api/products`, {
                    headers: {
                        'Accept': 'application/ld+json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to load products');
                }

                const result = await response.json();
                const productList = result['hydra:member'] || result.member || [];

                // Extract ID from IRI and keep full permission objects
                const normalizedProducts = productList.map((product: any) => ({
                    id: product.id || parseInt(product['@id']?.split('/').pop() || '0'),
                    name: product.name,
                    description: product.description,
                    permissions: product.permissions || [],
                }));

                setProducts(normalizedProducts);
            } catch (err) {
                setLoadError(translate('companyOnboarding.errors.loadProductsFailed'));
            } finally {
                setLoading(false);
            }
        };

        loadProducts();
    }, [translate]);

    const selectedProduct = products.find(p => p.id === data.productId);

    const handleProductChange = (productId: number) => {
        const product = products.find(p => p.id === productId);
        updateData({
            productId: productId,
            productName: product?.name || '',
        });
    };

    const handleExpirationToggle = (hasExp: boolean) => {
        setHasExpiration(hasExp);
        if (!hasExp) {
            updateData({ licenseExpiresAt: '' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.licenseTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.licenseDescription')}
            </Typography>

            <FormControl fullWidth error={!!errors.productId} sx={{ mb: 3 }}>
                <InputLabel>{translate('companyOnboarding.fields.product')}</InputLabel>
                <Select
                    value={data.productId || ''}
                    label={translate('companyOnboarding.fields.product')}
                    onChange={(e) => handleProductChange(e.target.value as number)}
                >
                    {products.map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                            {product.name}
                        </MenuItem>
                    ))}
                </Select>
                {errors.productId && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.productId}
                    </Typography>
                )}
            </FormControl>

            {selectedProduct && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {selectedProduct.name}
                        </Typography>
                        {selectedProduct.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {selectedProduct.description}
                            </Typography>
                        )}
                        {selectedProduct.permissions && selectedProduct.permissions.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    {translate('companyOnboarding.fields.includedPermissions')}:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selectedProduct.permissions.slice(0, 10).map((perm, index) => (
                                        <Chip
                                            key={perm.id || perm['@id'] || index}
                                            label={perm.name || 'Unknown'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                    {selectedProduct.permissions.length > 10 && (
                                        <Chip
                                            label={`+${selectedProduct.permissions.length - 10} ${translate('companyOnboarding.fields.more')}`}
                                            size="small"
                                            color="primary"
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={hasExpiration}
                            onChange={(e) => handleExpirationToggle(e.target.checked)}
                        />
                    }
                    label={translate('companyOnboarding.fields.hasExpiration')}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                    {translate('companyOnboarding.fields.lifetimeLicenseInfo')}
                </Typography>
            </Box>

            {hasExpiration && (
                <TextField
                    label={translate('companyOnboarding.fields.licenseExpiresAt')}
                    type="date"
                    value={data.licenseExpiresAt}
                    onChange={(e) => updateData({ licenseExpiresAt: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        min: new Date().toISOString().split('T')[0],
                    }}
                />
            )}

            <TextField
                label="Max. Benutzer"
                type="number"
                value={data.maxUsers}
                onChange={(e) => updateData({ maxUsers: e.target.value })}
                fullWidth
                helperText="Maximale Anzahl der Benutzer für dieses Unternehmen. Leer = unbegrenzt."
                inputProps={{ min: 1 }}
                sx={{ mt: 2 }}
            />
        </Box>
    );
};
